import { useState, useMemo, useEffect } from 'react';
import { normalizeString } from '../utils/formatters';

const STORAGE_KEY = 'admin_productos_filtros';

/**
 * Hook para gestionar filtros de productos
 * @param {Array} productos - Lista de productos a filtrar
 * @param {Object} camposFiltro - Campos disponibles para filtrar
 * @returns {Object} { filtros, productosFiltrados, handleFiltroChange, limpiarFiltros, hayFiltrosActivos }
 */
export const useFiltros = (productos, camposFiltro = ['nombre', 'categoria', 'material', 'color']) => {
  // Cargar filtros desde localStorage al inicializar
  const [filtros, setFiltros] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validar que los campos guardados coincidan con los campos esperados
        const initial = {};
        camposFiltro.forEach(campo => {
          initial[campo] = parsed[campo] || '';
        });
        return initial;
      }
    } catch (error) {
      console.error('Error al cargar filtros desde localStorage:', error);
    }
    
    // Si no hay filtros guardados o hay error, usar valores por defecto
    const initial = {};
    camposFiltro.forEach(campo => {
      initial[campo] = '';
    });
    return initial;
  });

  // Guardar filtros en localStorage cada vez que cambien
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtros));
    } catch (error) {
      console.error('Error al guardar filtros en localStorage:', error);
    }
  }, [filtros]);

  const productosFiltrados = useMemo(() => {
    return productos.filter(producto => {
      return camposFiltro.every(campo => {
        const filtroValue = filtros[campo];
        if (!filtroValue) return true;

        const productoValue = producto[campo] || producto.nombre || producto.titulo || producto.modelo || producto.id || '';
        
        // Para todos los campos, usar igualdad exacta (incluyendo modelo)
        return normalizeString(productoValue) === normalizeString(filtroValue);
      });
    });
  }, [productos, filtros, camposFiltro]);

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const limpiarFiltros = () => {
    const cleared = {};
    camposFiltro.forEach(campo => {
      cleared[campo] = '';
    });
    setFiltros(cleared);
    // Limpiar también del localStorage
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error al limpiar filtros del localStorage:', error);
    }
  };

  const hayFiltrosActivos = useMemo(() => {
    return camposFiltro.some(campo => filtros[campo] !== '');
  }, [filtros, camposFiltro]);

  return {
    filtros,
    productosFiltrados,
    handleFiltroChange,
    limpiarFiltros,
    hayFiltrosActivos,
  };
};

